/**
 * @copyright Copyright 2022-2024 Epic Systems Corporation
 * @file Type declarations for QuestionnaireVersion
 * @module Epic.AppOrchard.Types.QuestionnaireVersion
 */

import { QuestionnaireType } from "ao/data";
import { ISelectionOption, QuestionnaireQuestionType } from ".";

export interface IParsedQuestionnaireVersion {
	id: number | null;
	name: string;
	questionnaireType: QuestionnaireType;
	startDate: Date;
	editableInEpicOnFhirEndDate: Date | null;
	categories: IParsedCategory[];
	hooks: IHookForVersion[];
	isInUse: boolean;
}

export interface IParsedCategory {
	id: number;
	title: string;
	questions: IQuestionModel[];
}

export interface IHookForVersion {
	id: number;
	type: QuestionnaireHookType;
	condition: string;
	emailTemplateId: number | null;
	catalogToUseId: number | null;
	actionError: string;
	conditionError: string;
	isExpanded: boolean;
}

interface IRawQuestionnaireVersion {
	name: string;
	questionnaireType: QuestionnaireType;
	startDate: Date;
	editableInEpicOnFhirEndDate: Date | null;
	questions: IQuestionForVersion[];
	hooks: IHookForVersion[];
	isInUse: boolean;
}

export type ISimpleQuestionnaireVersion = IRawQuestionnaireVersion & {
	id: number | null;
};

export type IQuestionnaireVersionModel = IRawQuestionnaireVersion & {
	id: number;
};

export interface IQuestionCategoryModel {
	id: number;
	title: string;
}

export interface IQuestionForVersion {
	id: number;
	displayCondition: string;
	indent: boolean;
}

export interface IQuestionModel {
	id: number;
	questionText: string;
	questionType: QuestionnaireQuestionType;
	categoryID: number;
	documentID: number;
	isPublished: boolean;
	usedByDuq: boolean;
	displayCondition: string;
	indent: boolean;
	options: IOptionModel[];
}

export interface IOptionModel {
	id: number;
	optionText: string;
}

export interface IConditionModel {
	index: number;
	question: IQuestionModel;
	optionId: number;
	isNegated: boolean;
	operator: ConditionOperator;
	warning: string;
	focus: boolean;
}

export interface IQuestionnaireVersionManagementModel {
	questionCategories: IQuestionCategoryModel[];
	questions: IQuestionModel[];
	questionnaireVersions: IQuestionnaireVersionModel[];
	emailTemplates: ISelectionOption[];
	catalogOptions: ISelectionOption[];
}

export interface IUpsertQuestionnaireVersionResponse {
	id: number;
	message: string;
}

export enum ConditionOperator {
	NULL = 0,
	AND = 1,
	OR = 2,
}

export enum QuestionnaireHookType {
	Unknown = -1,
	SendEmail = 0,
	SetCatalog = 1,
}

export enum DUQVisibility {
	Unset = 0,
	ShowDataUse = 1,
	DoNotShow = 2,
}
